import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListIdentifiers } from 'src/features/membership/api/identifier/useListIdentifiers';
import CellIdentifierActions from 'src/features/membership/components/cell/CellIdentifierActions';
import CreateIdentifierFormModal from 'src/features/membership/components/forms/identifier/CreateIdentifierFormModal';
import { MEMBERSHIP_USER_ACCESS_LEVEL, MembershipIdentifier } from 'src/features/membership/types';
import { identifierTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';

const columnHelper = createColumnHelper<MembershipIdentifier>();

type Props = {
    membershipId?: string;
    accessLevel?: MEMBERSHIP_USER_ACCESS_LEVEL;
    isClient?: boolean;
};
export default function IdentifiersTable({ membershipId, accessLevel, isClient = false }: Props) {
    const { formatMessage } = useIntl();
    const permissionAccessActiveStay = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const permissionManageMembershipIdentifiers = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const permissionAccessEntity = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const { querySearchOptions, ...search } = useSearch();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const canManageIdentifiers =
        permissionManageMembershipIdentifiers ||
        accessLevel === MEMBERSHIP_USER_ACCESS_LEVEL.MEMBER ||
        accessLevel === MEMBERSHIP_USER_ACCESS_LEVEL.ADMIN;

    const { parkingId } = useParking();
    const { data, isLoading } = useListIdentifiers({
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        parkingId,
        membershipId: membershipId ?? '',
        config: { enabled: !!membershipId },
    });

    const columns = [
        columnHelper.accessor('identifier.value', {
            enableSorting: true,
            enableHiding: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(identifiersMessages.identifier),
            },
        }),
        columnHelper.accessor('identifier.type', {
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(identifiersMessages.identifierType),
                badgeOptions: identifierTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('current_stay', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.activeStay),
            },
            cell: ({ row: { original } }) => {
                if (!original.current_stay) return '-';
                return (
                    <LinkWithIcon
                        to={`/${PATH.VISITORS}/active-stay/dashboard/${original.current_stay.id}`}
                        linkTitle={original.current_stay.ref}
                        hasPermissions={permissionAccessActiveStay}
                    />
                );
            },
        }),

        ...(!isClient
            ? [
                  columnHelper.accessor('owner.name', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      enableSorting: true,
                      cell: ({ row: { original } }) => {
                          if (!original.owner) return '-';

                          return (
                              <LinkWithIcon
                                  to={`/${PATH.ENTITIES}/${original.owner.type}/${original.owner.id}`}
                                  linkTitle={original.owner.name}
                                  hasPermissions={permissionAccessEntity}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(identifiersMessages.owner),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('is_archived', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(identifiersMessages.isArchived),
            },
        }),
        columnHelper.accessor('public_notes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(!isClient ? identifiersMessages.publicNotes : identifiersMessages.notes),
            },
        }),

        ...(!isClient
            ? [
                  columnHelper.accessor('private_notes', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      enableSorting: true,
                      meta: {
                          title: formatMessage(identifiersMessages.privateNotes),
                      },
                  }),
              ]
            : []),

        ...(canManageIdentifiers
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (props) => (
                          <CellIdentifierActions {...props} isClient={isClient} membershipId={membershipId} />
                      ),
                      maxSize: 120,
                  }),
              ]
            : []),
    ];
    return (
        <DataTable<MembershipIdentifier, any>
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isLoading={isLoading}
            columns={columns}
            data={data}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(identifiersMessages.identifiers),
                entryName: formatMessage(identifiersMessages.identifier).toLowerCase(),
                entryNames: formatMessage(identifiersMessages.identifiers).toLowerCase(),
                placeholder: formatMessage(identifiersMessages.identifier),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(canManageIdentifiers && {
                    newEntry: () => <CreateIdentifierFormModal isClient={isClient} membershipId={membershipId} />,
                }),
            }}
        />
    );
}
